"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export type ToastVariant = "default" | "success" | "destructive";

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ToastItem = {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
  actions?: ToastAction[];
};

type ToastContextValue = {
  toast: (t: Omit<ToastItem, "id">) => void;
  dismiss: (id: string) => void;
};

const ToastContext = React.createContext<ToastContextValue | null>(null);

function genId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const timersRef = React.useRef<Map<string, number>>(new Map());

  const dismiss = React.useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback(
    (t: Omit<ToastItem, "id">) => {
      const id = genId();
      const durationMs = t.durationMs ?? 7000;
      const item: ToastItem = { id, ...t };
      setToasts((prev) => [item, ...prev].slice(0, 3));

      const timer = window.setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  React.useEffect(() => {
    return () => {
      for (const timer of timersRef.current.values()) window.clearTimeout(timer);
      timersRef.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

function variantClasses(variant: ToastVariant | undefined) {
  switch (variant) {
    case "success":
      return "border-emerald-200 bg-emerald-50 text-emerald-950";
    case "destructive":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    default:
      return "border-border bg-card text-foreground";
  }
}

function Toaster({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed right-4 top-4 z-[60] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            "rounded-lg border shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/80",
            variantClasses(t.variant),
          )}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3 p-3">
            <div className="min-w-0 flex-1">
              {t.title ? (
                <div className="text-sm font-semibold leading-5">{t.title}</div>
              ) : null}
              {t.description ? (
                <div className="mt-0.5 text-sm leading-5 opacity-90">{t.description}</div>
              ) : null}

              {t.actions?.length ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {t.actions.map((a) => (
                    <button
                      key={a.label}
                      type="button"
                      className={cn(
                        "inline-flex h-8 items-center rounded-md border px-2.5 text-xs font-medium",
                        "border-border bg-background hover:bg-muted",
                      )}
                      onClick={() => a.onClick()}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
            >
              <span className="text-lg leading-none">×</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

